import React from 'react';
import { Sentry } from '../sentry';

/**
 * Top-level error boundary fallback shown when React throws an unhandled
 * render error. The error is automatically reported to Sentry by
 * <Sentry.ErrorBoundary>; this UI gives the user a way to recover.
 *
 * Wrapped around <App /> in src/index.js.
 */
function FallbackUI({ resetError }) {
  const reload = () => {
    try { resetError?.(); } catch { /* ignored */ }
    window.location.reload();
  };
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#000B18] text-white px-4"
      data-testid="app-error-fallback"
    >
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-[#FF1744]/15 border border-[#FF1744]/30 mx-auto flex items-center justify-center mb-5">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
            stroke="#FF1744" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <h1 className="font-outfit text-2xl font-semibold mb-2">Something went wrong</h1>
        <p className="text-[#A0B4CC] text-sm mb-6">
          We hit an unexpected error and our team has been notified. Please try reloading the page.
        </p>
        <button
          onClick={reload}
          className="px-5 py-2.5 rounded-lg bg-[#00E5FF] text-[#000B18] font-semibold text-sm hover:bg-[#00E5FF]/90 transition-colors"
          data-testid="app-error-reload-btn"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <FallbackUI resetError={resetError} />}
      beforeCapture={(scope) => scope.setTag('app_area', 'frontend')}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
